import {useDeleteMeterMutation, useUpdateMeterDnuMutation,} from "@ots-frontend/ots-data";
import {useCallback, useEffect} from "react";
import {notifications} from "@mantine/notifications";
import { ResultTypeFrom } from "@reduxjs/toolkit/query";
import {useTranslation} from "react-i18next";
import {useDebouncedState} from "@mantine/hooks";
import {modals} from "@mantine/modals";
import {Text} from "@mantine/core";
import {useApp} from "../providers/RegionProvider/RegionProvider";

export type UseMeterUpdateProps = {}

export type UseMeterUpdateResult = {
  updateMeterDnu: (meterId: string, dnu: boolean) => void
  deleteMeter: (meterId: string, onSuccess: (() => void) | undefined) => void
}

export const useMeterMutation = (props: UseMeterUpdateProps = {}): UseMeterUpdateResult => {
  const {regionId} = useApp()
  const {t} = useTranslation(["common"])

  // state
  const [_updating, _setUpdating] = useDebouncedState(false, 500)
  const [_deleting, _setDeleting] = useDebouncedState(false, 500)

  // possible mutations
  const [_updateMeterDnu, {isLoading: isUpdatingDnu}] = useUpdateMeterDnuMutation();
  const [_deleteMeter, {isLoading: isDeleting}] = useDeleteMeterMutation()

  useEffect(() => {
    _setUpdating(isUpdatingDnu)
  }, [isUpdatingDnu, _setUpdating])

  useEffect(() => {
    _setDeleting(isDeleting)
  }, [isDeleting, _setDeleting])

  useEffect(() => {
    if (_updating) {
      notifications.show({
        id: "useMeterUpdate_updating",
        title: t("common.status.updating", "Updating") + " " + t("common.entity.meter", "Meter"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("useMeterUpdate_updating")
    }
  }, [_updating, t]);

  useEffect(() => {
    if (_deleting) {
      notifications.show({
        id: "useMeterUpdate_deleting",
        title: t("common.status.deleting", "Deleting") + " " + t("common.entity.meter", "Meter"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("useMeterUpdate_deleting")
    }
  }, [_deleting, t]);

  const handleUpdateResult = useCallback((promise: Promise<ResultTypeFrom<any>>) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.meter", "Meter") + " " + t("common.status.updated", "Updated"),
          message: ""
        });
      })
      .catch((rejected) =>
        notifications.show({
          color: "red",
          title: t("common.error", "Error"),
          message: rejected?.data?.detail ?? t("common.error", "Error", {ns: "common"}),
        })
      );
  }, [t])

  const updateMeterDnu = useCallback((meterId: string, dnu: boolean) => {
    if (regionId) {
      const promise = _updateMeterDnu({
        meterId: meterId,
        regionId: regionId,
        dnu: dnu
      }).unwrap()
      handleUpdateResult(promise)
    }
  }, [_updateMeterDnu, regionId, handleUpdateResult])

  const handleDeleteResult = useCallback((promise: Promise<ResultTypeFrom<any>>, onSuccess: (() => void) | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.meter", "Meter") + " " + t("common.status.deleted", "Deleted"),
          message: ""
        });

        if (onSuccess) {
          onSuccess()
        }
      })
      .catch((rejected) =>
        notifications.show({
          color: "red",
          title: t("common.error", "Error"),
          message: rejected?.data?.detail ?? t("common.error", "Error", {ns: "common"}),
        })
      );
  }, [t])

  const handleDeleteConfirm = useCallback((meterId: string, onSuccess: (() => void) | undefined = undefined) => {
    const promise = _deleteMeter({id: meterId}).unwrap()
    handleDeleteResult(promise, onSuccess)
  }, [_deleteMeter, handleDeleteResult])

  const deleteMeter = useCallback((meterId: string, onSuccess: (() => void) | undefined) => {
    modals.openConfirmModal({
      title: t("common.action.delete", "Delete") + " " + t("common.entity.meter", "Meter"),
      centered: true,
      children: (
        <Text size="sm" p="md">
          {t("common.action.delete.meter.confirm", "Are you sure you want to delete this meter?")}
        </Text>
      ),
      labels: {
        confirm: t("common.action.delete", "Delete"),
        cancel: t("common.action.cancel", "Cancel"),
      },
      confirmProps: {color: 'red'},
      onConfirm: () => {
        handleDeleteConfirm(meterId, onSuccess)
      },
    })
  }, [handleDeleteConfirm, t])

  return {
    updateMeterDnu: updateMeterDnu,
    deleteMeter: deleteMeter,
  }
}
